import React, { forwardRef } from 'react';

import type { TextInput as RNTextInput } from 'react-native';

import { ValidatedTextInput } from '../inputs';
import type { ValidatedTextInputProps } from '../inputs';

export interface PhoneInputProps extends Omit<
  ValidatedTextInputProps,
  'format' | 'customErrorMessages' | 'ariaLabelledBy' | 'ariaLabel'
> {
  /**
   * Analytics name for tracking events
   */
  analyticsName: string;
  /**
   * Custom error message for invalid phone number
   * @default "Please enter a valid phone number."
   */
  invalidPhoneError?: string;
  /**
   * Custom error message for required field
   * @default "This field is required."
   */
  fieldRequiredError?: string;
  /**
   * ARIA labelledBy attribute
   */
  ariaLabelledBy?: string;
}

/**
 * PhoneInput is a specialized phone number input component with validation and formatting.
 * It extends ValidatedTextInput with phone-specific validation and formatting.
 *
 * Features:
 * - Phone number validation using ValidatedTextInput's built-in format
 * - Automatic formatting (e.g., 000-000-0000)
 * - International format support
 * - Consistent styling
 *
 * @example
 * ```tsx
 * <PhoneInput
 *   analyticsName="my-app-registration"
 *   title="Phone number"
 *   getValue={(value) => setPhone(value.unformattedValue)}
 *   required={true}
 * />
 * ```
 */
export const PhoneInput = forwardRef<RNTextInput, PhoneInputProps>(
  (
    {
      analyticsName,
      invalidPhoneError = 'Please enter a valid phone number.',
      fieldRequiredError = 'This field is required.',
      title = 'Phone number',
      size = 'medium',
      placeholder = '000-000-0000',
      ariaLabelledBy,
      ...props
    },
    ref,
  ) => {
    return (
      <ValidatedTextInput
        ref={ref}
        id="phone"
        analyticsName={`${analyticsName}-phone`}
        format="telephoneNumber"
        size={size}
        placeholder={placeholder}
        customErrorMessages={{
          fieldRequired: fieldRequiredError,
          invalidPhoneNumber: invalidPhoneError,
        }}
        {...(ariaLabelledBy ? { ariaLabelledBy } : { title })}
        {...props}
      />
    );
  },
);

PhoneInput.displayName = 'PhoneInput';
